import { useState } from "react";

import { enterpriseContext, type EcContext, type EcDiscovery } from "../api/resources";
import { useAsync } from "../lib/useAsync";
import { Card, ErrorBanner, Field, Loading, Stat } from "./ui";

/**
 * Read health of the Enterprise Context link: binding, contract, composed packs, semantic gaps,
 * freshness. Everything shown is a local projection of what Enterprise Context answered —
 * never authority, and never editable here. Approval and publication live in Enterprise Context.
 */
export function EnterpriseContextCard({
  organizationId,
  canRefresh,
  canLink,
}: {
  organizationId: string;
  canRefresh: boolean;
  canLink: boolean;
}) {
  const context = useAsync(() => enterpriseContext.context(organizationId), [organizationId]);
  const discovery = useAsync(() => enterpriseContext.discovery(organizationId), [organizationId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [tenantSlug, setTenantSlug] = useState("");

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      context.reload();
      discovery.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const data = context.data;
  return (
    <Card
      title="Enterprise Context"
      actions={
        canRefresh && data && data.binding.status !== "UNCONFIGURED" ? (
          <>
            <button
              disabled={busy}
              title="Read back what Enterprise Context did with the observations we proposed"
              onClick={() => void run(() => enterpriseContext.refreshGovernance(organizationId))}
            >
              Check review status
            </button>
            <button
              className="primary"
              disabled={busy}
              onClick={() => void run(() => enterpriseContext.refresh(organizationId))}
            >
              {busy ? "Refreshing…" : "Refresh"}
            </button>
          </>
        ) : undefined
      }
    >
      <ErrorBanner error={error ?? context.error} />
      {context.loading || !data ? (
        <Loading />
      ) : (
        <ContextBody data={data} discovery={discovery.data} />
      )}
      {data?.binding.status === "UNCONFIGURED" && canLink ? (
        <div className="row" style={{ alignItems: "flex-end", gap: 8, marginTop: 12 }}>
          <Field label="Enterprise Context tenant">
            <input
              value={tenantSlug}
              placeholder="sugar-bowl"
              onChange={(event) => setTenantSlug(event.target.value)}
            />
          </Field>
          <button
            disabled={busy || !tenantSlug}
            onClick={() => void run(() => enterpriseContext.link(organizationId, tenantSlug))}
          >
            Link
          </button>
        </div>
      ) : null}
      {data && data.binding.status !== "UNCONFIGURED" ? (
        <p className="small muted" style={{ marginBottom: 0, marginTop: 12 }}>
          What is shown here is what Enterprise Context answered, not company truth. Survey
          proposes; only an Enterprise Context steward approves and an administrator publishes.
          Tenants are created in Enterprise Context, never from here.
        </p>
      ) : null}
    </Card>
  );
}

const BINDING_LABEL: Record<EcContext["binding"]["status"], string> = {
  UNCONFIGURED: "Not linked",
  UNVERIFIED: "Not yet verified",
  VERIFIED: "Verified",
  INCOMPATIBLE: "Incompatible contract",
  ERROR: "Error",
};

function bindingTone(status: EcContext["binding"]["status"]): "ok" | "warn" | "danger" | undefined {
  if (status === "VERIFIED") return "ok";
  if (status === "UNVERIFIED") return "warn";
  if (status === "UNCONFIGURED") return undefined;
  return "danger";
}

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "never";
}

function ContextBody({ data, discovery }: { data: EcContext; discovery: EcDiscovery | undefined }) {
  const { binding, contract, packs, gaps, gap_summary, freshness, definitions, target_warnings } = data;
  if (binding.status === "UNCONFIGURED") {
    return (
      <p className="small muted" style={{ marginBottom: 0 }}>
        This organization is not linked to an Enterprise Context tenant. Linking records a tenant
        slug; it is verified against Enterprise Context before anything is read.
      </p>
    );
  }
  const contractLabel =
    contract.discovery_export_server === null
      ? "unknown"
      : `${contract.discovery_export_server} ${contract.compatible ? "compatible" : "incompatible"}`;
  return (
    <>
      <div className="grid grid--stats" style={{ marginBottom: 12 }}>
        <Stat
          label="Connection"
          value={BINDING_LABEL[binding.status]}
          tone={bindingTone(binding.status)}
          hint={binding.last_verified_at ? `verified ${when(binding.last_verified_at)}` : undefined}
        />
        <Stat label="Tenant" value={binding.tenant_slug ?? "—"} />
        <Stat
          label="Contract"
          value={contractLabel}
          tone={contract.compatible === false ? "danger" : contract.compatible ? "ok" : undefined}
          hint={`Survey speaks ${contract.discovery_export_expected}`}
        />
        <Stat
          label="Semantic gaps"
          value={`${gap_summary.open ?? 0} open`}
          tone={(gap_summary.open ?? 0) > 0 ? "warn" : "ok"}
          hint="reported by Enterprise Context"
        />
      </div>

      {binding.error ? (
        <div className="banner banner--danger" data-testid="ec-binding-error">
          <strong>{binding.error_class}</strong>: {binding.error}
        </div>
      ) : null}

      <div className="grid grid--two">
        <div>
          <h3>Industry context</h3>
          {packs.length === 0 ? (
            <p className="small muted">No packs read yet.</p>
          ) : (
            <ol className="small" data-testid="ec-packs">
              {packs.map((pack) => (
                <li key={pack.key}>
                  <span className="mono">{pack.key}</span> <span className="muted">{pack.version}</span>
                  {pack.description ? <span className="muted"> — {pack.description}</span> : null}
                </li>
              ))}
            </ol>
          )}
        </div>
        <div>
          <h3>Freshness</h3>
          <dl className="small" data-testid="ec-freshness">
            <dt>Last successful refresh</dt>
            <dd>{when(freshness.last_successful_refresh_at)}</dd>
            <dt>Current refresh</dt>
            <dd>
              {freshness.refresh_status === "NEVER"
                ? "never run"
                : freshness.refresh_status === "OK"
                  ? "succeeded"
                  : freshness.refresh_status === "PARTIAL"
                    ? "partial — packs refreshed, gaps kept from the last success"
                    : "failed — showing the last successful state"}
              {freshness.refresh_status !== "OK" && freshness.last_refresh_at
                ? ` (${when(freshness.last_refresh_at)})`
                : null}
            </dd>
            {freshness.stale ? (
              <>
                <dt>Status</dt>
                <dd>
                  <span className="badge badge--open">stale</span>
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      {discovery && discovery.source === "enterprise-context" ? (
        <div className="small" style={{ marginTop: 12 }} data-testid="ec-discovery">
          <strong>Discovery modules from these packs:</strong>{" "}
          {discovery.module_keys.length ? discovery.module_keys.join(", ") : "none yet"}
          <span className="muted"> — {discovery.reason}</span>
        </div>
      ) : null}

      {definitions.length > 0 ? (
        <div className="small" style={{ marginTop: 8 }} data-testid="ec-definitions">
          <strong>{definitions.length}</strong> published definition
          {definitions.length === 1 ? "" : "s"} for concepts our questions target; matching questions
          are not asked again unless a session requests recertification.
        </div>
      ) : null}

      {target_warnings.length > 0 ? (
        <div className="banner banner--warn" data-testid="ec-target-warnings">
          {target_warnings.length} question target{target_warnings.length === 1 ? "" : "s"} name a
          concept this tenant cannot see:{" "}
          {[...new Set(target_warnings.map((w) => w.concept))].join(", ")}. They stay local until the
          pack composition or the question changes — nothing is guessed.
        </div>
      ) : null}

      {gaps.length > 0 ? (
        <details style={{ marginTop: 12 }}>
          <summary className="small">
            {gaps.length} semantic gap{gaps.length === 1 ? "" : "s"} Enterprise Context reports
          </summary>
          <ul className="small" data-testid="ec-gaps">
            {gaps.map((gap) => (
              <li key={gap.gap_key}>
                <span className="mono">{gap.concept}</span>
                {gap.member ? <span className="mono">.{gap.member}</span> : null} — {gap.gap.replace(/_/g, " ")}{" "}
                <span className="badge badge--neutral">{gap.severity}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}
